import imgHelpfulTip1 from '../../../../images/img_helpful_tip_1.svg';
import imgHelpfulTip2 from '../../../../images/img_helpful_tip_2.svg';
import imgHelpfulTip3 from '../../../../images/img_helpful_tip_3.svg';

/**
 * API-supported search options (OAR-1996).
 * Case and opinion detail pages use redirect links with: caseName:, court_id:, docketNumber:.
 * The API also supports judge: (cases + opinions) and citation: (opinions only).
 *
 * Cases: keyword (q), court_id, docketNumber, caseName, judge; Advanced: party_name, atty_name, jurisdictions, order_by.
 * Opinions: keyword, citation, docketNumber, caseName, judge; no party/attorney; jurisdictions, order_by.
 */

export const FAQ_ITEMS = [
  {
    id: '0',
    question: 'What is the Court Case Search?',
    answer:
      'Court Case Search allows you to look up publicly available federal, state and supreme court records, including details about legal cases, parties involved, case outcomes, and more.',
  },
  {
    id: '1',
    question: 'What types of cases can I find?',
    answer:
      'You can search for a wide range of federal, state, and local cases, including:',
    list: [
      'Civil and criminal cases',
      'Appellate court decisions',
      'U.S. Supreme Court cases',
      'Bankruptcy filings (selected courts)',
    ],
  },
  {
    id: '2',
    question: 'What information will I see in the results?',
    answer: 'Search results may include:',
    list: [
      'Case titles and docket numbers',
      'Court jurisdictions',
      'Names of parties and attorneys involved',
      'Key dates and case summaries',
      'Case outcomes and written opinions (if available)',
    ],
  },
  {
    id: '3',
    question: 'Can I search by name?',
    answer:
      'Yes. You can search using the name of a person, business, or organization involved in a case. More specific searches (e.g., full name, court jurisdiction) may yield better results.',
  },
  {
    id: '4',
    question: 'How current is the information?',
    answer:
      'Court records are updated regularly, but some cases may take time to appear depending on how and when they are filed.',
  },
  {
    id: '5',
    question: "Why can't I find a specific case?",
    answer: 'There could be a few reasons:',
    list: [
      'The case may be sealed or not publicly available',
      'It may be filed in a local/state court not included in the search',
      'Details may be entered differently than expected (e.g., name spelling)',
    ],
  },
];

export const FAQ_DISCLAIMER =
  'PLEASE BE ADVISED: Court case information contained in our reports may not always be 100% up-to-date, accurate or complete, since data is pulled from records maintained by various state, county, municipal and other government agencies and their contents may not be fully digitized, updated or comprehensive. Please use our results as no more than a point of departure and certainly no substitute for your own due diligence.';

export const HELPFUL_TIPS_V2 = [
  {
    id: '0',
    title: 'Tip #1',
    text: 'Use quotes for exact matches. Typing "John A. Smith" improves accuracy compared to John Smith.',
    icon: imgHelpfulTip1,
  },
  {
    id: '1',
    title: 'Tip #2',
    text: 'You can lookup company or institutions involved in legal cases by entering their full name.',
    icon: imgHelpfulTip2,
  },
  {
    id: '2',
    title: 'Tip #3',
    text: 'If you have a docket number or case ID, enter it directly for the most precise match.',
    icon: imgHelpfulTip3,
  },
];

export const SEARCH_OPERATORS = {
  title: 'Using Operators',
  description:
    'Operators can be used to search for complex queries. Pairing with "Advanced Options" can help focus on your desired results.',
  operators: [
    {
      id: 'and',
      symbol: 'AND',
      name: 'Intersection',
      example: 'e.g John AND Johnathan',
      exampleParts: ['e.g John ', 'AND', ' Johnathan'],
    },
    {
      id: 'or',
      symbol: 'OR',
      name: 'Union',
      example: 'e.g Red OR Blue',
      exampleParts: ['e.g Red ', 'OR', ' Blue'],
    },
    {
      id: 'negation',
      symbol: '-',
      name: 'Negation',
      example: 'e.g Whiskey -Bourbon',
      exampleParts: ['e.g Whiskey ', '-', 'Bourbon'],
    },
    {
      id: 'phrase',
      symbol: '" "',
      name: 'Phrase Search',
      example: 'e.g "Selling" products',
      exampleParts: ['e.g ', '"', 'Selling', '"', ' products'],
    },
    {
      id: 'grouped',
      symbol: '()',
      name: 'Grouped Quotes',
      example: 'e.g (Phone OR Cell)',
      exampleParts: ['e.g (Phone ', 'OR', ' Cell)'],
    },
    {
      id: 'field',
      symbol: ':',
      name: 'Field Search',
      example: 'e.g Revenue in: 10k',
      exampleParts: ['e.g Revenue in', ':', ' 10k'],
    },
    {
      id: 'wildcard',
      symbol: '*, ?',
      name: 'Wildcard',
      example: 'e.g fee and law*',
      exampleParts: ['e.g fee and law', '*'],
    },
    {
      id: 'range',
      symbol: '[x TO y]',
      name: 'Fuzzy & Proximity',
      example: 'e.g Harlem TO Astoria',
      exampleParts: ['e.g Harlem ', 'TO', ' Astoria'],
    },
  ],
};

export const SORT_OPTIONS = [
  { value: 'score_desc', display: 'Relevance' },
  { value: 'dateFiled_desc', display: 'Newest Cases First' },
  { value: 'dateFiled_asc', display: 'Oldest Cases First' },
  { value: 'citeCount_desc', display: 'Most Cited First' },
  { value: 'citeCount_asc', display: 'Least Cited First' },
];

/** Class action search: UI sort; optional `order_by` is also sent when present. */
export const CLASS_ACTION_SORT_OPTIONS = [
  { value: 'dateFiled_desc', display: 'Newest Cases First' },
  { value: 'dateFiled_asc', display: 'Oldest Cases First' },
];

export const NO_RESULTS_TIPS = [
  { id: 'spelling', text: 'Check your spelling.' },
  { id: 'filters', text: 'Try fewer filters.' },
  { id: 'quotes', text: 'Remove quotes if you used them.' },
];

export const JURISDICTION_TABS = [
  { key: 'federal_appellate', label: 'Federal Appellate' },
  { key: 'federal_district', label: 'Federal District' },
  { key: 'bankruptcy', label: 'Bankruptcy' },
  { key: 'state', label: 'State' },
  { key: 'more', label: 'More' },
] as const;

// Maps UI sort option values to API order_by parameters
export const ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  dateFiled_desc: 'dateFiled desc',
  dateFiled_asc: 'dateFiled asc',
  citeCount_desc: 'citeCount desc',
  citeCount_asc: 'citeCount asc',
} as const;

// Reverse mapping: API format -> UI format
export const REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'dateFiled desc': 'dateFiled_desc',
  'dateFiled asc': 'dateFiled_asc',
  'citeCount desc': 'citeCount_desc',
  'citeCount asc': 'citeCount_asc',
};

// Oral Arguments: UI -> API
export const ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  dateArgued_desc: 'dateArgued desc',
  dateArgued_asc: 'dateArgued asc',
} as const;

// Oral Arguments: API -> UI
export const ORAL_ARGUMENTS_REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'dateArgued desc': 'dateArgued_desc',
  'dateArgued asc': 'dateArgued_asc',
};

// Judge Search: UI -> API
export const JUDGE_SEARCH_ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  name_reverse_asc: 'name_reverse asc',
  dob_desc_name_reverse_asc: 'dob desc,name_reverse asc',
  dob_asc_name_reverse_asc: 'dob asc,name_reverse asc',
  dod_desc_name_reverse_asc: 'dod desc,name_reverse asc',
} as const;

// Judge Search: API -> UI
export const JUDGE_SEARCH_REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'name_reverse asc': 'name_reverse_asc',
  'dob desc,name_reverse asc': 'dob_desc_name_reverse_asc',
  'dob asc,name_reverse asc': 'dob_asc_name_reverse_asc',
  'dod desc,name_reverse asc': 'dod_desc_name_reverse_asc',
};

// Display text for sort options
export const ORDER_BY_DISPLAY_MAP: Record<string, string> = {
  score_desc: 'Relevance',
  'score desc': 'Relevance',
  dateFiled_desc: 'Newest Cases First',
  'dateFiled desc': 'Newest Cases First',
  dateFiled_asc: 'Oldest Cases First',
  'dateFiled asc': 'Oldest Cases First',
  citeCount_desc: 'Most Cited First',
  'citeCount desc': 'Most Cited First',
  citeCount_asc: 'Least Cited First',
  'citeCount asc': 'Least Cited First',
};

export const ORIGIN_MAP: { [key: string]: string } = {
  '1': '(1) Original Proceeding',
  '2': '(2) Removed from State Court',
  '3': '(3) Remanded from Appellate Court',
  '4': '(4) Reinstated or Reopened',
  '5': '(5) Transferred from another District',
  '6': '(6) Multi-District Litigation',
  '7': '(7) Appeal of Magistrate Judgment to a District Judge (since SY81)',
  '8': '(8) 2nd Reopening (since SY83)',
  '9': '(9) 3rd Reopening (since SY83)',
  '-8': '(-8) Missing',
  '0': '(0) Unknown value from 1970-1987 data',
};

export const JURISDICTION_MAP: { [key: string]: string } = {
  '1': '(1) U.S. Plaintiff',
  '2': '(2) U.S. Defendant',
  '3': '(3) Federal Question',
  '4': '(4) Diversity of Citizenship',
  '5': '(5) Local Question',
};

export const NATURE_OF_SUIT_MAP: { [key: string]: string } = {
  '100': '(100) Indemnity of Admiralty Cases',
  '110': '(110) Insurance',
  '112': '(112) Unknown',
  '114': '(114) Unknown',
  '120': '(120) Marine Contract Actions',
  '122': '(122) Unknown',
  '125': '(125) Unknown',
  '130': '(130) Miller Act',
  '140': '(140) Negotiable Instruments',
  '141': '(141) Unknown',
  '150': '(150) Overpayments & Enforcement of Judgments',
  '151': '(151) Overpayments Under the Medicare Act',
  '152': '(152) Recovery of Defaulted Student Loans',
  '153': '(153) Recovery of Overpayments of Vet Benefits',
  '160': "(160) Stockholder's Suits",
  '170': '(170) Unknown',
  '190': '(190) Other Contract Actions',
  '191': '(191) Employee Relations',
  '192': '(192) State Fair Trade Act',
  '193': '(193) Hospital Care Act',
  '195': '(195) Contract Product Liability',
  '210': '(210) Land Condemnation',
  '220': '(220) Foreclosure',
  '230': '(230) Rent, Lease, Ejectment',
  '240': '(240) Torts to Land',
  '241': '(241) Unknown',
  '245': '(245) Tort Product Liability',
  '290': '(290) Other Real Property Actions',
  '310': '(310) Airplane Personal Injury',
  '315': '(315) Airplane Product Liability',
  '320': '(320) Assault, Libel, and Slander',
  '330': "(330) Federal Employers' Liability",
  '340': '(340) Marine Personal Injury',
  '341': '(341) Unknown',
  '345': '(345) Marine - Product Liability',
  '350': '(350) Motor Vehicle Personal Injury',
  '351': '(351) Unknown',
  '355': '(355) Motor Vehicle Product Liability',
  '360': '(360) Other Personal Injury',
  '361': "(361) Workman's Compensation (Industrial Accident Board)",
  '362': '(362) Medical Malpractice',
  '365': '(365) Personal Injury - Product Liability',
  '368': '(368) Asbestos Personal Injury - Product Liability',
  '370': '(370) Other Fraud',
  '371': '(371) Truth in Lending',
  '380': '(380) Other Personal Property Damage',
  '385': '(385) Property Damage - Product Liability',
  '390': '(390) Unknown',
  '400': '(400) State Re-apportionment',
  '410': '(410) Antitrust',
  '416': '(416) Unknown',
  '420': '(420) Trustee',
  '421': '(421) Transfer (915b)',
  '422': '(422) Bankruptcy Appeals Rule 28 USC 158',
  '423': '(423) Bankruptcy Withdrawal 28 USC 157',
  '430': '(430) Banks and Banking',
  '440': '(440) Other Civil Rights',
  '441': '(441) Civil Rights Voting',
  '442': '(442) Civil Rights Jobs',
  '443': '(443) Civil Rights Accommodations',
  '444': '(444) Civil Rights Welfare',
  '450': '(450) Interstate Commerce',
  '451': '(451) Unknown',
  '460': '(460) Deportation',
  '461': '(461) Deportation - Review',
  '470': '(470) Civil (RICO)',
  '510': '(510) Prisoner Petitions -Vacate Sentence',
  '511': '(511) Unknown',
  '520': '(520) Parole Board Review',
  '530': '(530) Prisoner Petitions -Habeas Corpus',
  '531': '(531) Unknown',
  '540': '(540) Prisoner Petitions -Mandamus and Other',
  '550': '(550) Prisoner -Civil Rights',
  '610': '(610) Agricultural Acts',
  '620': '(620) Food and Drug Acts',
  '630': '(630) Liquor Laws',
  '640': '(640) Railroad and Trucks',
  '650': '(650) Airline Regulations',
  '660': '(660) Occupational Safety/Health',
  '690': '(690) Other Forfeiture and Penalty Suits',
  '710': '(710) Fair Labor Standards Act',
  '711': '(711) Unknown',
  '720': '(720) Labor/Management Relations Act',
  '730': '(730) Labor/Management Report & Disclosure',
  '740': '(740) Railway Labor Act',
  '750': '(750) Welfare Pension Plan Disclosure',
  '790': '(790) Other Labor Litigation',
  '791': '(791) Employee Retirement Income Security Act',
  '810': '(810) Selective Service',
  '820': '(820) Copyright',
  '830': '(830) Patent',
  '840': '(840) Trademark',
  '850': '(850) Securities, Commodities, Exchange',
  '860': '(860) Social Security',
  '861': '(861) HIA (1395 FF)/ Medicare',
  '862': '(862) Black Lung',
  '863': '(863) D.I.W.C./D.I.W.W.',
  '864': '(864) S.S.I.D.',
  '865': '(865) R.S.I.',
  '870': '(870) Tax Suits',
  '871': '(871) IRS 3rd Party Suits 26 USC 7609',
  '875': '(875) Customer Challenge 12 USC 3410',
  '876': '(876) Unknown',
  '880': '(880) Review or Enforcement',
  '890': '(890) Other Statutory Actions',
  '891': '(891) Agricultural Acts',
  '892': '(892) Economic Stabilization Act',
  '893': '(893) Environmental Matters',
  '894': '(894) Energy Allocation Act',
  '895': '(895) Freedom of Information Act of 1974',
  '900': '(900) Appeal of Fee -Equal Access to Justice',
  '910': '(910) Domestic Relations',
  '920': '(920) Insanity',
  '930': '(930) Probate',
  '940': '(940) Substitute Trustee',
  '950': '(950) Constitutionality of State Statutes',
  '970': '(970) NARA, Title III',
  '971': '(971) Unknown',
  '990': '(990) Other',
  '991': '(991) Unknown',
  '992': '(992) Local Jurisdictional Appeal',
  '999': '(999) Miscellaneous',
};

export const DISPOSITION_MAP: { [key: string]: string } = {
  '0': '(0) Transfer to Another District',
  '1': '(1) Remanded to State Court',
  '2': '(2) Want of Prosecution',
  '3': '(3) Lack of Jurisdiction',
  '4': '(4) Default',
  '5': '(5) Consent',
  '6': '(6) Motion Before Trial',
  '7': '(7) Jury Verdict',
  '8': '(8) Directed Verdict',
  '9': '(9) Court Trial',
  '10': '(10) Multi District Litigation Transfer',
  '11': '(11) Remanded to U.S. Agency',
  '12': '(12) Voluntarily',
  '13': '(13) Settled',
  '14': '(14) Other',
  '15': '(15) Award of Arbitrator',
  '16': '(16) Stayed Pending Bankruptcy',
  '17': '(17) Other',
  '18': '(18) Statistical Closing',
  '-9': '(-9) Data Not Collected (SY72-SY78)',
};

export const PRO_SE_MAP: { [key: string]: string } = {
  '0': '(0) No Pro Se Plaintiffs or Defendants',
  '1': '(1) Pro Se Plaintiffs, but No Pro Se Defendants',
  '2': '(2) Pro Se Defendants, but No Pro Se Plaintiffs',
  '3': '(3) Both Pro Se Plaintiffs & Defendants',
  '-8': '(-8) Missing',
};

export const ATTORNEY_ROLES = {
  ATTORNEY_TO_BE_NOTICED: 1,
  LEAD_ATTORNEY: 2,
  SPECIAL_ROLE: 4,
  PRO_SE: 10,
} as const;

export const ATTORNEY_ROLE_DISPLAY_MAP: { [key: number]: string } = {
  [ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]: 'ATTORNEY TO BE NOTICED',
  [ATTORNEY_ROLES.LEAD_ATTORNEY]: 'LEAD ATTORNEY',
  [ATTORNEY_ROLES.SPECIAL_ROLE]: 'LEAD ATTORNEY',
  [ATTORNEY_ROLES.PRO_SE]: 'PRO SE',
};
